import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDataFlowStore } from '@/stores/dataflowStore';

interface WebSocketMessage {
  type:
    | 'instance.started'
    | 'instance.progress'
    | 'instance.completed'
    | 'instance.failed'
    | 'instance.cancelled'
    | 'instance.log'
    | 'runtime.stream.event'
    | 'runtime.stream.state'
    | 'runtime.stream.snapshot'
    | 'batch.updated'
    | 'queue.updated';
  payload?: Record<string, unknown>;
  [key: string]: unknown;
}

export function useDataFlowWebSocket() {
  const queryClient = useQueryClient();
  const {
    appendInstanceLog,
    appendWorkerLogTail,
    appendRuntimeStreamEvent,
    setRuntimeStreamEvents,
    setRuntimeStreamState,
  } = useDataFlowStore();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const shouldReconnectRef = useRef(true);
  const MAX_RECONNECT_ATTEMPTS = 5;

  const connect = useCallback(() => {
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.error('[DataFlow] Max WebSocket reconnect attempts reached');
      return;
    }
    const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws/dataflow`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
        console.log('[DataFlow] WebSocket connected');
        ws.send(JSON.stringify({ action: 'subscribe', channel: 'runtime' }));
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          const payload =
            message.payload && typeof message.payload === 'object'
              ? message.payload
              : (message as Record<string, unknown>);

          switch (message.type) {
            case 'instance.started':
            case 'instance.completed':
            case 'instance.failed':
            case 'instance.cancelled': {
              const id = Number(payload.id || 0);
              queryClient.invalidateQueries({ queryKey: ['dataflow', 'instances'] });
              if (id > 0) {
                queryClient.invalidateQueries({ queryKey: ['dataflow', 'instances', id] });
              }
              queryClient.invalidateQueries({ queryKey: ['dataflow', 'batches'] });
              queryClient.invalidateQueries({ queryKey: ['dataflow', 'timeline'] });
              queryClient.invalidateQueries({ queryKey: ['dataflow', 'queue'] });
              break;
            }

            case 'instance.progress': {
              const updates: Record<string, unknown> = {};
              if (typeof payload.progress_pct === 'number') updates.progress_pct = payload.progress_pct;
              if (typeof payload.progress_current === 'number') updates.progress_current = payload.progress_current;
              if (typeof payload.progress_total === 'number') updates.progress_total = payload.progress_total;
              queryClient.setQueryData(
                ['dataflow', 'instances', payload.id],
                (old: unknown) =>
                  old && typeof old === 'object' ? { ...old, ...updates } : old
              );
              break;
            }

            case 'batch.updated':
              queryClient.invalidateQueries({ queryKey: ['dataflow', 'batches'] });
              break;

            case 'queue.updated':
              queryClient.invalidateQueries({ queryKey: ['dataflow', 'queue'] });
              break;

            case 'instance.log':
              {
                const instanceId = Number(payload.instance_id || 0);
                const ts = String(payload.ts || new Date().toISOString());
                const level = String(payload.level || 'INFO');
                const messageText = String(payload.message || '');
                if (instanceId > 0) {
                  appendInstanceLog(
                    instanceId,
                    {
                      ts,
                      level,
                      message: messageText,
                    }
                  );
                }
                appendWorkerLogTail({
                  ts,
                  level,
                  instanceId,
                  message: messageText,
                });
              }
              break;

            case 'runtime.stream.state': {
              setRuntimeStreamState({
                streaming: Boolean(payload.streaming),
                active_instances: Number(payload.active_instances || 0),
                updated_at: String(payload.updated_at || ""),
              });
              break;
            }

            case 'runtime.stream.snapshot': {
              const eventsRaw = payload.events;
              const normalized = Array.isArray(eventsRaw)
                ? eventsRaw.map((item) => {
                    const row =
                      item && typeof item === "object"
                        ? (item as Record<string, unknown>)
                        : {};
                    return {
                      ts: String(row.ts || ""),
                      kind: String(row.kind || "log"),
                      phase: String(row.phase || ""),
                      level: String(row.level || "INFO"),
                      instanceId: Number(row.instance_id || 0),
                      templateId: Number(row.template_id || 0),
                      batchId: Number(row.batch_id || 0),
                      taskType: String(row.task_type || "task"),
                      title: String(row.title || ""),
                      message: String(row.message || ""),
                      metrics:
                        row.metrics && typeof row.metrics === "object" && !Array.isArray(row.metrics)
                          ? (row.metrics as Record<string, unknown>)
                          : undefined,
                    };
                  })
                : [];
              setRuntimeStreamEvents(normalized);
              break;
            }

            case 'runtime.stream.event': {
              const row = payload;
              appendRuntimeStreamEvent({
                ts: String(row.ts || new Date().toISOString()),
                kind: String(row.kind || "log"),
                phase: String(row.phase || ""),
                level: String(row.level || "INFO"),
                instanceId: Number(row.instance_id || 0),
                templateId: Number(row.template_id || 0),
                batchId: Number(row.batch_id || 0),
                taskType: String(row.task_type || "task"),
                title: String(row.title || ""),
                message: String(row.message || ""),
                metrics:
                  row.metrics && typeof row.metrics === "object" && !Array.isArray(row.metrics)
                    ? (row.metrics as Record<string, unknown>)
                    : undefined,
              });
              break;
            }
          }
        } catch (error) {
          console.error('[DataFlow] Failed to parse WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        if (!shouldReconnectRef.current) {
          return;
        }
        console.log('[DataFlow] WebSocket disconnected, retrying in 5s...');
        reconnectAttemptsRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(connect, 5000);
      };

      ws.onerror = (error) => {
        console.error('[DataFlow] WebSocket error:', error);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('[DataFlow] Failed to create WebSocket:', error);
      reconnectAttemptsRef.current += 1;
      reconnectTimeoutRef.current = setTimeout(connect, 5000);
    }
  }, [
    queryClient,
    appendInstanceLog,
    appendWorkerLogTail,
    appendRuntimeStreamEvent,
    setRuntimeStreamEvents,
    setRuntimeStreamState,
  ]);

  useEffect(() => {
    shouldReconnectRef.current = true;
    connect();
    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);
}
